import { useCallback, useEffect, useReducer, useState } from 'react'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core'
import InputAdornment from '@material-ui/core/InputAdornment'
import DeleteIcon from '@material-ui/icons/Delete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import GetAppIcon from '@material-ui/icons/GetApp'
import SearchIcon from '@material-ui/icons/Search'

import ConfirmationModal from '../../components/ConfirmationModal'
import ContactFileModal from '../../components/ContactFileModal'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import toastError from '../../errors/toastError'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './style'

const reducer = (state, action) => {
  if (action.type === 'LOAD_FILES') {
    const files = action.payload
    const newFiles = []

    files.forEach(file => {
      const fileIndex = state.findIndex(f => f.id === file.id)
      if (fileIndex !== -1) {
        state[fileIndex] = file
      } else {
        newFiles.push(file)
      }
    })

    return [...state, ...newFiles]
  }

  if (action.type === 'UPDATE_FILES') {
    const file = action.payload
    const fileIndex = state.findIndex(f => f.id === file.id)

    if (fileIndex !== -1) {
      state[fileIndex] = file
      return [...state]
    } else {
      return [file, ...state]
    }
  }

  if (action.type === 'DELETE_FILE') {
    const fileId = action.payload

    const fileIndex = state.findIndex(f => f.id === fileId)
    if (fileIndex !== -1) {
      state.splice(fileIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const ContactFiles = () => {
  const classes = useStyles()

  // const { user } = useContext(AuthContext)

  const [loading, setLoading] = useState(false)
  // const [selectedFile, setSelectedFile] = useState(null)
  const [deletingFile, setDeletingFile] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [files, dispatch] = useReducer(reducer, [])
  const [fileModalOpen, setFileModalOpen] = useState(false)
  // const [contacts, setContacts] = useState([])

  // const { list: listHelps } = useHelps()

  // Fetch all files for the company (optimized)
  const fetchFiles = useCallback(async () => {
    try {
      setLoading(true)
      const [contactFilesResponse, generalFilesResponse] = await Promise.all([
        api.get('/contact-files/list/all'),
        api.get('/files')
      ])
      
      const allFiles = []
      
      // Handle contact files
      if (contactFilesResponse.data?.files && Array.isArray(contactFilesResponse.data.files)) {
        allFiles.push(...contactFilesResponse.data.files)
      }
      
      console.log('Contact files response:', contactFilesResponse.data)
      console.log('General files response:', generalFilesResponse.data)
      
      // Handle general files - pode vir como data.files ou diretamente como array
      let generalFiles = []
      if (generalFilesResponse.data?.files && Array.isArray(generalFilesResponse.data.files)) {
        generalFiles = generalFilesResponse.data.files
      } else if (Array.isArray(generalFilesResponse.data)) {
        generalFiles = generalFilesResponse.data
      }
      
      console.log('Parsed general files:', generalFiles)
      
      // Marcar arquivos gerais com contact: null e gerar ID único se necessário
      const markedGeneralFiles = generalFiles.map((file, index) => {
        const processedFile = {
          ...file,
          // Se não tem ID, gerar um único baseado no índice
          id: file.id || `general-file-${Date.now()}-${index}`,
          // Se não tem fileName, usar o name ou url
          fileName: file.fileName || file.name || file.url?.split('/').pop() || `Arquivo ${index + 1}`,
          contact: null
        }
        console.log('Processed general file:', processedFile)
        return processedFile
      })
      
      allFiles.push(...markedGeneralFiles)
      
      console.log('All files loaded:', allFiles)
      dispatch({ type: 'LOAD_FILES', payload: allFiles })
      setLoading(false)
    } catch (err) {
      console.error('Error fetching files:', err)
      toastError(err)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchFiles()
  }, [fetchFiles])

  const handleDeleteFile = useCallback(async (fileId, isGeneralFile = false) => {
    try {
      const endpoint = isGeneralFile ? `/files/${fileId}` : `/contact-files/${fileId}`
      await api.delete(endpoint)
      dispatch({ type: 'DELETE_FILE', payload: fileId })
      toast.success(i18n.t('contacts.deleteFileSuccess'))
    } catch (err) {
      console.error('Error deleting file:', err)
      toastError(err)
    }
    setDeletingFile(null)
    setConfirmModalOpen(false)
  }, [])

  const handleOpenFile = useCallback((url) => {
    window.open(url, '_blank')
  }, [])

  // Group files by contact and separate general files
  const groupedData = files.reduce((acc, file) => {
    console.log('Processing file for grouping:', file)
    // Se o arquivo não tiver contact ou contact for null, é um arquivo geral
    if (!file.contact || file.contact === null) {
      // This is a general file
      console.log('File identified as GENERAL FILE:', file)
      acc.generalFiles.push(file)
    } else {
      // This is a contact file
      console.log('File identified as CONTACT FILE:', file)
      const contactId = file.contact.id
      if (!acc.contacts[contactId]) {
        acc.contacts[contactId] = {
          contactId,
          contactName: file.contact.name,
          contactNumber: file.contact.number,
          files: []
        }
      }
      acc.contacts[contactId].files.push(file)
    }
    return acc
  }, { contacts: {}, generalFiles: [] })

  const groupedFiles = groupedData.contacts
  const generalFiles = groupedData.generalFiles

  console.log('Final grouped data:', { generalFiles, contactsCount: Object.keys(groupedFiles).length })

  const filteredGroups = Object.entries(groupedFiles).filter(([_, group]) => {
    const searchLower = searchParam.toLowerCase()
    return (
      group.contactName.toLowerCase().includes(searchLower) ||
      group.contactNumber.includes(searchLower) ||
      group.files.some(f => f.fileName.toLowerCase().includes(searchLower))
    )
  })

  const filteredGeneralFiles = generalFiles.filter(file => {
    const searchLower = searchParam.toLowerCase()
    return file.fileName.toLowerCase().includes(searchLower)
  })

  return (
    <MainContainer>
      <MainHeader>
        <Typography variant="h5">{i18n.t('contacts.files')}</Typography>
        <MainHeaderButtonsWrapper>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setFileModalOpen(true)}
          >
            {i18n.t('contacts.addFile')}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.paper} style={{ marginBottom: 16 }}>
        <TextField
          placeholder={i18n.t('contacts.searchFiles')}
          type="search"
          value={searchParam}
          onChange={(e) => setSearchParam(e.target.value)}
          variant="outlined"
          size="small"
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {loading && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
          <CircularProgress />
        </Box>
      )}

      {!loading && filteredGroups.length === 0 && filteredGeneralFiles.length === 0 && (
        <Typography variant="body1" align="center" style={{ marginTop: 20 }}>
          {i18n.t('contacts.noFiles')}
        </Typography>
      )}

      {!loading && filteredGeneralFiles.length > 0 && (
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box display="flex" alignItems="center" width="100%">
              <Typography variant="h6" style={{ marginRight: 10 }}>
                {i18n.t('contacts.generalFile') || 'Arquivos Gerais'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                - {filteredGeneralFiles.length} {filteredGeneralFiles.length === 1 ? 'arquivo' : 'arquivos'}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <Box width="100%">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{i18n.t('contacts.fileName')}</TableCell>
                    <TableCell align="right">{i18n.t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredGeneralFiles.map(file => (
                    <TableRow key={`general-${file.url}`}>
                      <TableCell>{file.fileName}</TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          color="primary"
                          startIcon={<GetAppIcon />}
                          onClick={() => handleOpenFile(file.url)}
                          style={{ marginRight: 8 }}
                        >
                          {i18n.t('common.download')}
                        </Button>
                        <Button
                          size="small"
                          color="secondary"
                          startIcon={<DeleteIcon />}
                          onClick={() => {
                            setDeletingFile({ ...file, isGeneralFile: true })
                            setConfirmModalOpen(true)
                          }}
                        >
                          {i18n.t('common.delete')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </AccordionDetails>
        </Accordion>
      )}

      {!loading &&
        filteredGroups.map(([contactId, group]) => (
          <Accordion key={contactId} defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" width="100%">
                <Typography variant="h6" style={{ marginRight: 10 }}>
                  {group.contactName}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  ({group.contactNumber}) - {group.files.length} {group.files.length === 1 ? 'arquivo' : 'arquivos'}
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box width="100%">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{i18n.t('contacts.fileName')}</TableCell>
                      <TableCell align="right">{i18n.t('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {group.files.map(file => (
                      <TableRow key={`contact-${contactId}-${file.url}`}>
                        <TableCell>{file.fileName}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            color="primary"
                            startIcon={<GetAppIcon />}
                            onClick={() => handleOpenFile(file.url)}
                            style={{ marginRight: 8 }}
                          >
                            {i18n.t('common.download')}
                          </Button>
                          <Button
                            size="small"
                            color="secondary"
                            startIcon={<DeleteIcon />}
                            onClick={() => {
                              setDeletingFile({ ...file, isGeneralFile: false })
                              setConfirmModalOpen(true)
                            }}
                          >
                            {i18n.t('common.delete')}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}

      <ContactFileModal
        open={fileModalOpen}
        onClose={() => setFileModalOpen(false)}
        onSave={() => {
          setFileModalOpen(false)
          fetchFiles()
        }}
      />

      <ConfirmationModal
        title={i18n.t('contacts.deleteFileTitle')}
        open={confirmModalOpen}
        onClose={() => {
          setConfirmModalOpen(false)
          setDeletingFile(null)
        }}
        onConfirm={() => handleDeleteFile(deletingFile.id, deletingFile?.isGeneralFile || false)}
      >
        {i18n.t('contacts.confirmDeleteFile')}
      </ConfirmationModal>
    </MainContainer>
  )
}

export default ContactFiles
